import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert, Badge, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, FormHelperText, Grid, InputLabel, LinearProgress,
  MenuItem, Select, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import { AddAPhoto, PhotoLibrary } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { employeePortalAPI } from '../utils/api';
import { generateFaceEmbedding } from '../utils/faceEmbedding';
import type { EmployeeImagePose, EmployeePortalImage } from '../types/employeePortal';
import FaceCapture from './FaceCapture';

const poses: EmployeeImagePose[] = ['front', 'left-side', 'right-side'];
const supportedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
const maxBytes = 8 * 1024 * 1024;

type FieldErrors = Partial<Record<'file' | 'pose' | 'image_rank' | 'embedding', string>>;

const EmployeePhotos: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('auth');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<EmployeePortalImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [preview, setPreview] = useState<EmployeePortalImage | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [pose, setPose] = useState<EmployeeImagePose>('front');
  const [rank, setRank] = useState('1');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);

  const handleAuthError = useCallback((error: unknown) => {
    if (!axios.isAxiosError(error)) return false;
    if (error.response?.status === 401) {
      navigate('/employee/login', { replace: true });
      return true;
    }
    if (error.response?.status === 403 && error.response.data?.must_change_password === true) {
      localStorage.setItem('employee_must_change_password', 'true');
      navigate('/employee/change-password', { replace: true });
      return true;
    }
    return false;
  }, [navigate]);

  const loadImages = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await employeePortalAPI.getImages();
      setImages(response.data || []);
    } catch (error) {
      if (!handleAuthError(error)) setLoadError(t('employeePortal.photos.loadError'));
    } finally {
      setLoading(false);
    }
  }, [handleAuthError, t]);

  useEffect(() => { void loadImages(); }, [loadImages]);

  const resetUpload = () => {
    setOpen(false);
    setFile(null);
    setPose('front');
    setRank(String(Math.min(images.length + 1, 10)));
    setFieldErrors({});
    setProgress(0);
  };

  const closeUpload = () => {
    if (!busy) resetUpload();
  };

  const validate = () => {
    const errors: FieldErrors = {};
    const numericRank = Number(rank);
    if (!file) errors.file = t('employeePortal.photos.validation.fileRequired');
    else if (!supportedTypes.has(file.type)) errors.file = t('employeePortal.photos.validation.fileType');
    else if (file.size > maxBytes) errors.file = t('employeePortal.photos.validation.fileSize');
    if (!poses.includes(pose)) errors.pose = t('employeePortal.photos.validation.poseRequired');
    if (!Number.isInteger(numericRank) || numericRank < 1 || numericRank > 10) {
      errors.image_rank = t('employeePortal.photos.validation.rank');
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || !validate() || !file) return;
    setBusy(true);
    setProgress(0);
    setFieldErrors({});
    try {
      const embedding = await generateFaceEmbedding(file);
      const body = new FormData();
      body.append('file', file);
      body.append('pose', pose);
      body.append('image_rank', rank);
      body.append('embedding', embedding);
      await employeePortalAPI.uploadImage(body, setProgress);
      resetUpload();
      setNotice({ severity: 'success', message: t('employeePortal.photos.uploadSuccess') });
      await loadImages();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        const backendErrors = error.response.data?.errors || {};
        const mapped: FieldErrors = {};
        for (const key of ['file', 'pose', 'image_rank', 'embedding'] as const) {
          const value = backendErrors[key];
          if (value) mapped[key] = Array.isArray(value) ? value[0] : String(value);
        }
        setFieldErrors(mapped);
      } else if (!handleAuthError(error)) {
        const message = error instanceof Error && error.message === 'NO_FACE_DETECTED'
          ? t('employeePortal.photos.validation.noFace')
          : t('employeePortal.photos.uploadError');
        setNotice({ severity: 'error', message });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCaptureComplete = async (capturedImages: Array<{ file: File; pose: string; image_rank: number }>) => {
    setShowCapture(false);
    setBusy(true);
    setProgress(0);
    try {
      for (let index = 0; index < capturedImages.length; index += 1) {
        const captured = capturedImages[index];
        if (!supportedTypes.has(captured.file.type) || captured.file.size > maxBytes ||
            !poses.includes(captured.pose as EmployeeImagePose) || captured.image_rank < 1 || captured.image_rank > 10) {
          throw new Error('CAPTURE_VALIDATION_FAILED');
        }
        const embedding = await generateFaceEmbedding(captured.file);
        const body = new FormData();
        body.append('file', captured.file);
        body.append('pose', captured.pose);
        body.append('image_rank', String(captured.image_rank));
        body.append('embedding', embedding);
        await employeePortalAPI.uploadImage(body, uploadProgress => {
          setProgress(Math.round(((index + uploadProgress / 100) / capturedImages.length) * 100));
        });
      }
      setNotice({ severity: 'success', message: t('employeePortal.photos.scanSuccess', { count: capturedImages.length }) });
      await loadImages();
    } catch (error) {
      if (!handleAuthError(error)) {
        const message = error instanceof Error && error.message === 'NO_FACE_DETECTED'
          ? t('employeePortal.photos.validation.noFace')
          : error instanceof Error && error.message === 'CAPTURE_VALIDATION_FAILED'
            ? t('employeePortal.photos.validation.capture')
            : t('employeePortal.photos.scanError');
        setNotice({ severity: 'error', message });
      }
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  return (
    <Card elevation={0} sx={{ border: '1px solid var(--an-border-light)', borderRadius: 'var(--an-radius-md)' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
          <Box>
            <Typography variant="h6" fontWeight={800}>{t('employeePortal.photos.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('employeePortal.photos.readOnly')}</Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <Button variant="outlined" startIcon={<PhotoLibrary />} disabled={busy} onClick={() => setShowCapture(true)}>
              {t('employeePortal.photos.faceScanner')}
            </Button>
            <Button variant="contained" startIcon={<AddAPhoto />} disabled={busy} onClick={() => { setRank(String(Math.min(images.length + 1, 10))); setOpen(true); }}>
              {t('employeePortal.photos.uploadPhoto')}
            </Button>
          </Stack>
        </Stack>

        {busy && !open && (
          <Alert severity="info" sx={{ mb: 2 }} role="status" aria-live="polite">
            <Typography variant="body2">{progress > 0 ? t('employeePortal.photos.uploading', { progress }) : t('employeePortal.photos.processingFace')}</Typography>
            <LinearProgress variant={progress > 0 ? 'determinate' : 'indeterminate'} value={progress} sx={{ mt: 1 }} />
          </Alert>
        )}

        {loading ? (
          <Box role="status" aria-label={t('employeePortal.photos.loading')} textAlign="center" py={5}><CircularProgress /></Box>
        ) : loadError ? (
          <Alert severity="error" action={<Button color="inherit" onClick={loadImages}>{t('employeePortal.photos.retry')}</Button>}>{loadError}</Alert>
        ) : images.length === 0 ? (
          <Box textAlign="center" py={5} sx={{ bgcolor: 'var(--an-surface-muted)', borderRadius: 2 }}>
            <PhotoLibrary color="disabled" sx={{ fontSize: 48 }} />
            <Typography fontWeight={700}>{t('employeePortal.photos.emptyTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('employeePortal.photos.emptyDescription')}</Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {images.map(image => (
              <Grid key={image.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card variant="outlined">
                  <Button onClick={() => setPreview(image)} aria-label={t('employeePortal.photos.previewLabel', { pose: t(`employeePortal.photos.poses.${image.pose}`), rank: image.image_rank })} sx={{ display: 'block', p: 0, width: '100%' }}>
                    <Box component="img" src={image.public_url} alt={t('employeePortal.photos.imageAlt', { pose: t(`employeePortal.photos.poses.${image.pose}`) })} sx={{ width: '100%', height: 210, objectFit: 'cover', display: 'block' }} />
                  </Button>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight={700}>{t(`employeePortal.photos.poses.${image.pose}`)}</Typography>
                      <Badge color={image.is_active ? 'success' : 'default'} badgeContent={t(`employeePortal.photos.${image.is_active ? 'active' : 'inactive'}`)} sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none' } }} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{t('employeePortal.photos.rankValue', { rank: image.image_rank })}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('employeePortal.photos.uploadedDate', { date: new Date(image.created_at).toLocaleDateString(i18n.language) })}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>

      <Dialog open={open} onClose={() => closeUpload()} fullWidth maxWidth="sm" aria-labelledby="employee-photo-upload-title">
        <Box component="form" onSubmit={submit} noValidate>
          <DialogTitle id="employee-photo-upload-title">{t('employeePortal.photos.uploadPhoto')}</DialogTitle>
          <DialogContent><Stack spacing={2.5} pt={1}>
            <Box>
              <Button component="label" variant="outlined" disabled={busy}>
                {t('employeePortal.photos.chooseImage')}<input ref={fileInputRef} hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={event => { setFile(event.target.files?.[0] || null); setFieldErrors(current => ({ ...current, file: undefined })); }} />
              </Button>
              <Typography variant="body2" ml={1} component="span">{file?.name || t('employeePortal.photos.noFileChosen')}</Typography>
              {fieldErrors.file && <FormHelperText error role="alert">{fieldErrors.file}</FormHelperText>}
            </Box>
            <FormControl error={Boolean(fieldErrors.pose)} required>
              <InputLabel id="employee-photo-pose-label">{t('employeePortal.photos.pose')}</InputLabel>
              <Select labelId="employee-photo-pose-label" label={t('employeePortal.photos.pose')} value={pose} onChange={event => setPose(event.target.value as EmployeeImagePose)} disabled={busy}>
                {poses.map(value => <MenuItem key={value} value={value}>{t(`employeePortal.photos.poses.${value}`)}</MenuItem>)}
              </Select>
              {fieldErrors.pose && <FormHelperText role="alert">{fieldErrors.pose}</FormHelperText>}
            </FormControl>
            <TextField label={t('employeePortal.photos.rank')} type="number" value={rank} onChange={event => setRank(event.target.value)} inputProps={{ min: 1, max: 10, step: 1 }} error={Boolean(fieldErrors.image_rank)} helperText={fieldErrors.image_rank || t('employeePortal.photos.rankHelp')} disabled={busy} required />
            {fieldErrors.embedding && <Alert severity="error" role="alert">{fieldErrors.embedding}</Alert>}
            {busy && <Box role="status" aria-live="polite"><Typography variant="body2">{progress > 0 ? t('employeePortal.photos.uploading', { progress }) : t('employeePortal.photos.processingFace')}</Typography><LinearProgress variant={progress > 0 ? 'determinate' : 'indeterminate'} value={progress} /></Box>}
          </Stack></DialogContent>
          <DialogActions><Button onClick={() => closeUpload()} disabled={busy}>{t('employeePortal.photos.cancel')}</Button><Button type="submit" variant="contained" disabled={busy}>{busy ? t('employeePortal.photos.pleaseWait') : t('employeePortal.photos.upload')}</Button></DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="md"><DialogTitle>{t('employeePortal.photos.previewTitle')}</DialogTitle><DialogContent>{preview && <Box component="img" src={preview.public_url} alt={t('employeePortal.photos.enlargedImageAlt', { pose: t(`employeePortal.photos.poses.${preview.pose}`) })} sx={{ maxWidth: '100%', maxHeight: '75vh', display: 'block' }} />}</DialogContent><DialogActions><Button onClick={() => setPreview(null)} autoFocus>{t('employeePortal.photos.close')}</Button></DialogActions></Dialog>
      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice(null)}><Alert severity={notice?.severity || 'success'} action={notice?.severity === 'error' ? <Button color="inherit" onClick={() => setOpen(true)}>{t('employeePortal.photos.retry')}</Button> : undefined}>{notice?.message}</Alert></Snackbar>
      {showCapture && (
        <FaceCapture
          show={showCapture}
          onHide={() => setShowCapture(false)}
          onComplete={handleCaptureComplete}
          startingRank={Math.min(images.length + 1, 6)}
        />
      )}
    </Card>
  );
};

export default EmployeePhotos;
